import React from "react";
import InstructorLayout from "../../layouts/InstructorLayout";
import { Eye, Pencil, Plus } from "lucide-react";
import ComingSoonOverlay from "../../components/ComingSoonOverlay";
import {
  QuizCard1,
  QuizCard2,
  QuizCard3,
  QuizCard4,
} from "../../components/instructor/icons";

const stats = [
  {
    title: "Total Quizzes",
    value: 3,
    icon: QuizCard1,
    bg: "bg-[#DBEAFE]",
    iconColor: "text-blue-500",
    valueColor: "text-[#111827]",
  },
  {
    title: "Pending Drafts",
    value: 2,
    icon: QuizCard2,
    bg: "bg-[#FFEDD5]",
    iconColor: "text-orange-500",
    valueColor: "text-[#EA580C]",
  },
  {
    title: "Scheduled Releases",
    value: 2,
    icon: QuizCard3,
    bg: "bg-[#DCFCE7]",
    iconColor: "text-green-500",
    valueColor: "text-[#16A34A]",
  },
  {
    title: "Avg. Completion",
    value: "87%",
    icon: QuizCard4,
    bg: "bg-[#F3E8FF]",
    iconColor: "text-purple-500",
    valueColor: "text-[#089BC4]",
  },
];

const quizzes = [
  {
    title: "fdfv",
    status: "published",
    badge: "Saved",
    questions: "3 questions",
    duration: "60 minutes",
    created: "10/15/2025",
    course: "Advanced JavaScript",
    module: "Module 1",
    release: "Immediate release",
    performance: "0 attempts",
  },
  {
    title: "Web Development Quiz 1 (Copy) (Copy)",
    status: "draft",
    badge: "Saved",
    questions: "0 questions",
    duration: "60 minutes",
    created: "10/15/2025",
    course: "React Development",
    module: "Module 1",
    release: "Scheduled: 2024-02-01",
    performance: "0 attempts",
  },
];

const getStatusClasses = (status) =>
  status === "published"
    ? "bg-[#DCFCE7] text-[#15803D]"
    : "bg-[#FFEDD5] text-[#C2410C]";

const QuizActions = () => (
  <div className="flex items-center gap-4">
    <button type="button" aria-label="Edit quiz">
      <Pencil size={18} className="text-[#2563EB]" />
    </button>
    <button type="button" aria-label="View quiz">
      <Eye size={18} className="text-[#16A34A]" />
    </button>
  </div>
);

const InstructorQuiz = () => {
  return (
    <InstructorLayout>
      <div className="relative min-h-screen">
        <div className="overflow-x-hidden px-4 pb-8 pt-4 sm:px-6 sm:pb-10 sm:pt-6 lg:px-10 xl:px-[60px]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-[#111827] sm:text-2xl">
              Instructor Quiz Management
            </h1>
            <p className="mt-1 text-sm text-[#4B5563] sm:text-base">
              Create, manage, and track course quizzes with AI assistance
            </p>
          </div>

          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#089BC4] px-5 py-3 text-sm text-white hover:bg-sky-700 sm:w-auto sm:px-6 sm:py-[14px] sm:text-base"
          >
            <Plus size={18} />
            Create New Quiz
          </button>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-4 sm:mt-6 sm:gap-6 md:grid-cols-2 xl:grid-cols-4">
          {stats.map((item, index) => {
            const Icon = item.icon;

            return (
              <div
                key={index}
                className="flex items-center justify-between rounded-lg border border-[#E5E7EB] bg-white px-4 py-5 shadow-[0_1px_2px_0_#0000000D] sm:py-6"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#4B5563]">
                    {item.title}
                  </p>
                  <h2 className={`mt-1 text-2xl font-bold ${item.valueColor}`}>
                    {item.value}
                  </h2>
                </div>

                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-lg ${item.bg}`}
                >
                  <Icon />
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-8 overflow-hidden rounded-xl border border-[#E5E7EB] bg-white sm:mt-10">
          <div className="border-b border-[#E5E7EB] px-4 py-4 sm:px-6 sm:py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:flex-wrap lg:items-center lg:gap-8 xl:gap-10">
              <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 lg:w-auto">
                <label className="text-sm font-medium text-[#374151]">
                  Course:
                </label>
                <select className="h-10 w-full rounded-lg border border-[#D1D5DB] px-4 text-sm outline-none sm:w-[200px]">
                  <option>All Courses</option>
                </select>
              </div>

              <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 lg:w-auto">
                <label className="text-sm font-medium text-[#374151]">
                  Status:
                </label>
                <select className="h-10 w-full rounded-lg border border-[#D1D5DB] px-4 text-sm outline-none sm:w-[140px]">
                  <option>All Status</option>
                </select>
              </div>

              <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3 lg:w-auto">
                <label className="text-sm font-medium text-[#374151]">
                  Date Range:
                </label>
                <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
                  <input
                    type="date"
                    className="h-10 w-full rounded-lg border border-[#D1D5DB] px-4 text-sm outline-none sm:w-auto"
                  />
                  <span className="hidden text-sm text-[#6B7280] sm:inline">
                    to
                  </span>
                  <input
                    type="date"
                    className="h-10 w-full rounded-lg border border-[#D1D5DB] px-4 text-sm outline-none sm:w-auto"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6 sm:py-6">
            <h2 className="text-2xl font-semibold text-[#111827] sm:text-[30px]">
              Quiz Overview
            </h2>

            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
              <span className="text-sm font-medium text-[#374151]">
                Sort by:
              </span>
              <select className="h-10 w-full rounded-lg border border-[#D1D5DB] px-4 text-sm outline-none sm:w-auto">
                <option>Date Created</option>
              </select>
            </div>
          </div>

          <div className="space-y-3 px-4 pb-4 md:hidden">
            {quizzes.map((quiz, index) => (
              <div
                key={`card-${index}`}
                className="rounded-xl border border-[#E5E7EB] bg-white p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold text-[#111827]">
                        {quiz.title}
                      </h3>
                      <span className="rounded-full bg-[#DBEAFE] px-3 py-1 text-xs font-medium text-[#2563EB]">
                        {quiz.badge}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-[#6B7280]">
                      {quiz.questions} • {quiz.duration}
                    </p>
                    <p className="mt-1 text-sm text-[#9CA3AF]">
                      Created: {quiz.created}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold capitalize ${getStatusClasses(quiz.status)}`}
                  >
                    {quiz.status}
                  </span>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex flex-wrap gap-x-2">
                    <span className="font-medium text-[#6B7280]">Course:</span>
                    <span className="text-[#111827]">
                      {quiz.course} • {quiz.module}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-2">
                    <span className="font-medium text-[#6B7280]">Release:</span>
                    <span className="text-[#111827]">{quiz.release}</span>
                  </div>
                  <div className="flex flex-wrap gap-x-2">
                    <span className="font-medium text-[#6B7280]">
                      Performance:
                    </span>
                    <span className="text-[#111827]">{quiz.performance}</span>
                  </div>
                </div>

                <div className="mt-4 border-t border-[#E5E7EB] pt-3">
                  <QuizActions />
                </div>
              </div>
            ))}
          </div>

          <div className="hidden px-4 pb-6 md:block sm:px-6">
            <div className="overflow-x-auto rounded-xl border border-[#E5E7EB]">
              <div className="min-w-[960px]">
                <div className="grid grid-cols-6 bg-[#F9FAFB] px-6 py-4 text-sm font-semibold text-[#6B7280]">
                  <div>Quiz Details</div>
                  <div>Status</div>
                  <div>Course</div>
                  <div>Release Info</div>
                  <div>Performance</div>
                  <div>Actions</div>
                </div>

                {quizzes.map((quiz, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-6 items-center border-t border-[#E5E7EB] px-6 py-6"
                  >
                    <div className="min-w-0 pr-4">
                      <div className="flex flex-wrap items-center gap-3">
                        <h3 className="font-semibold text-[#111827]">
                          {quiz.title}
                        </h3>
                        <span className="rounded-full bg-[#DBEAFE] px-3 py-1 text-xs font-medium text-[#2563EB]">
                          {quiz.badge}
                        </span>
                      </div>
                      <p className="mt-2 text-sm text-[#6B7280]">
                        {quiz.questions} • {quiz.duration}
                      </p>
                      <p className="mt-1 text-sm text-[#9CA3AF]">
                        Created: {quiz.created}
                      </p>
                    </div>

                    <div>
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold capitalize ${getStatusClasses(quiz.status)}`}
                      >
                        {quiz.status}
                      </span>
                    </div>

                    <div className="min-w-0 pr-4">
                      <p className="font-medium text-[#111827]">
                        {quiz.course}
                      </p>
                      <p className="text-sm text-[#4B5563]">{quiz.module}</p>
                    </div>

                    <div className="min-w-0 pr-4">
                      <p className="text-[#111827]">{quiz.release}</p>
                    </div>

                    <div>
                      <p className="text-[#111827]">{quiz.performance}</p>
                    </div>

                    <div>
                      <QuizActions />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        </div>

        <ComingSoonOverlay
          variant="instructor"
          title="Quiz Management"
          description="We're putting the finishing touches on Quiz Management. Soon you'll be able to create quizzes, schedule releases, and track student performance."
          buttonText="Coming Soon"
        />
      </div>
    </InstructorLayout>
  );
};

export default InstructorQuiz;
